// @ts-strict-ignore
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';
import { forkJoin, from, Observable, of, take } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { ApiActivityHierarchyEmissionsInventoryResponse } from '../../api/core/models';
import { ActionsService, ActivityHierarchyService } from '../../api/core/services';
import { ActivityHierarchyResponse } from '../../api/data-definitions/models/api/activity-hierarchy-response';
import { DEFAULT_LANGUAGE, GPC_HIERARCHY_NAME, TRANSITION_ELEMENT_ASSETS } from '../../app.constants';
import { hasSelectedTE } from '../../functions/client-model/client-node/has-selected-te';
import { holdsActiveCustomModel } from '../../functions/client-model/client-node/holds-active-custom-model';
import { filterOnSwimlane } from '../../functions/filter-on-swimlane';
import { emitTruthyOnceAndComplete } from '../../functions/rxjs-pipes/emit-truthy-once-and-complete';
import { ClientAction } from '../../model/client-model/client-action';
import { ClientBoard } from '../../model/client-model/client-board';
import { ClientFeatureFlagName } from '../../model/client-model/client-feature-flag';
import { ClientNode } from '../../model/client-model/client-node';
import { CustomTargetModelData } from '../../model/custom-model/custom-target-model-data';
import { Dict } from '../../model/dict';
import { Language } from '../../model/enums/language';
import { IndicatorListItem } from '../../model/indicator-list-item';
import { createNodesAndIndicators } from '../../public/dashboard/shared/functions/dashboard-mappers/create-nodes-and-indicators';
import { getChildren } from '../../public/dashboard/shared/functions/get-children';
import { ClientBoardContainer } from '../../public/dashboard/shared/model/client-board-container';
import { DashboardV4 } from '../../public/dashboard/shared/model/dashboard-v4';
import { DashboardV3 } from '../../public/dashboard/v3/dashboard-v3';
import { ActivityHierarchyTreeService } from '../../services/activity-hierarchy-tree.service';
import { ModelV2Service } from '../../services/backend-service/model-v2.service';
import { ModelService } from '../../services/backend-service/model.service';
import { BackendFeatureFlagsService } from '../../services/backend/backend-feature-flags.service';
import { Tree } from '../../services/backend/model/tree';
import {
  generateModelTranslationKey,
  ModelTranslationOutputType,
} from '../../services/climate-engine/ccc-model-utility/generate-model-translation-key';
import { BoardFacadeService } from '../../services/store/board/board-facade.service';
import { InventoriesFacadeService } from '../../services/store/inventories/inventories-facade.service';
import { TranslationService } from '../../services/translation-service';
import { ActionsFacadeService } from '../../store/actions/actions-facade.service';
import { ActivityHierarchiesFacadeService } from '../../store/activity-hierarchies/activity-hierarchies.facade.service';
import { ScenariosFacadeService } from '../../store/scenarios/scenarios.facade.service';
import { TransitionElementsFacadeService } from '../../store/transition-elements/transition-elements.facade.service';
import { TransitionStoriesFacadeService } from '../../store/transition-stories/transition-stories.facade.service';
import { getNodes } from './functions/get-nodes';
import { toIdDict } from './functions/to-id-dict';
import { ActivatedRouteDataForDashboard } from './model/activated-route-data-for-dashboard';
import { DashboardAdditionalData } from './model/dashboard-additional-data';
import { getBaselineInventoryValues, RawBoardData } from './raw-board-data';
import { CustomModelDataService } from './services/custom-model-data/custom-model-data.service';
import { DashboardCalculationService } from './services/dashboard-calculation/dashboard-calculation.service';
import { DashboardV4Service } from './services/dashboard-v4/dashboard-v4.service';
import { DashboardV3Service } from './services/dashboard.service/dashboard-v3.service';
import { LabelCreatorV4Service } from './services/label-creator-v4/label-creator-v4.service';

@Injectable({
  providedIn: 'root',
})
export class PreviewDashboardResolver {
  // Stores
  private _transitionElementFacadeService = inject(TransitionElementsFacadeService);
  private _transitionStoriesFacadeService = inject(TransitionStoriesFacadeService);
  private _scenariosFacadeService = inject(ScenariosFacadeService);

  // Properties
  private _gpcHierarchy$ = toObservable(
    this._activityHierarchiesFacadeService.getActivityHierarchyByName(GPC_HIERARCHY_NAME)
  );
  //Workaround for the stores not initiated.
  // Needed on first load since the resolver will stall if the stores aren't filled and then no store will get the data needed for the resolver.

  // Trigger the load of scenarios so we can get the scenario id.
  private scenarios = this._scenariosFacadeService.listScenarios();
  // Will return the transition elements when the selected scenario is available.
  private teSignal = computed(() => {
    const selected = this._scenariosFacadeService.selectedScenario();
    return this._transitionElementFacadeService.getTransitionElements(selected?.id);
  });
  //
  private _transitionElement$ = toObservable(this.teSignal());
  private _transitionStoriesHierarchy$ = toObservable(
    this._transitionStoriesFacadeService.loadStoriesForFullTransitionHierarchy()
  );

  constructor(
    private _actionsFacadeService: ActionsFacadeService,
    private _actionsService: ActionsService,
    private _activityHierarchiesFacadeService: ActivityHierarchiesFacadeService,
    private _activityHierarchyService: ActivityHierarchyService,
    private _activityHierarchyTreeService: ActivityHierarchyTreeService,
    private _boardFacadeService: BoardFacadeService,
    private _clientModelService: ModelService,
    private _customModelDataService: CustomModelDataService,
    private _dashboardCalculationService: DashboardCalculationService,
    private _dashboardV3Service: DashboardV3Service,
    private _dashboardV4Service: DashboardV4Service,
    private _featureFlagsService: BackendFeatureFlagsService,
    private _httpClient: HttpClient,
    private _inventoryFacadeService: InventoriesFacadeService,
    private _labelCreatorV4Service: LabelCreatorV4Service,
    private _translationService: TranslationService,
    private _modelV2Service: ModelV2Service
  ) {}

  getAdditionalData(route: ActivatedRouteSnapshot, isV5: boolean): Observable<DashboardAdditionalData> {
    return this._resolveBoard(route).pipe(
      map((data) => {
        const boardContainer = this._createBoardContainerV4(data);
        return this._createAdditionalData(boardContainer, isV5, data.tree);
      })
    );
  }

  resolve(route: ActivatedRouteDataForDashboard): Observable<DashboardV3 | DashboardV4> {
    return this._resolveBoard(route).pipe(
      switchMap((data) => {
        if (!data) {
          return of(null);
        }
        const ids: any[] = data.clientNodes.filter(hasSelectedTE).map((n) => n.nodeProperties.transitionElement);
        ids.push('default');
        const requests = ids.map((id) => {
          const url = `${TRANSITION_ELEMENT_ASSETS}icons/${id}.white.svg`;
          return this._httpClient
            .get(url, {
              responseType: 'text',
              headers: {
                anonymous: 'true',
              },
            })
            .pipe(catchError(() => from([undefined])));
        });
        const language = this._translationService.loadLanguageIfNeeded(
          data.board.boardSettings.internationalisation.defaultLanguage,
          'dashboard'
        );
        return forkJoin([...requests, language]).pipe(
          map((iconData: Array<string | undefined>) => {
            const dict = iconData.reduce((dict: Dict<string>, d: string, i: number) => {
              if (d) {
                const id = ids[i];
                dict[id] = d;
              }
              return dict;
            }, {});
            return [data, dict];
          }),
          map(([data, dict]: [RawBoardData, Dict<string>]) => {
            const isV3 = this._featureFlagsService.isEnabled(ClientFeatureFlagName.DashboardV3);
            const isV4 = this._featureFlagsService.isEnabled(ClientFeatureFlagName.DashboardV4);
            const isV5 = !isV3 && !isV4;
            const board = isV4 || isV5 ? this._toDashboardV4(data, isV5) : this._toDashboard(data, dict);
            this._setTTTitles(getNodes(board), data.board.boardSettings.internationalisation.defaultLanguage);
            return board;
          })
        );
      })
    );
  }

  /**
   * Group colour override can be added on top level nodes, if it is it will be applied to a node it will be added to all its children.
   * If a node has a groupColour set it will be used for the node colour.
   * @param board
   */
  private _applyGroupColour(board: ClientBoardContainer): void {
    const applyColourIfSet = (node: ClientNode, groupColour: string) => {
      if (groupColour) {
        node.nodeProperties.groupColour = groupColour;
        getChildren(board, node).forEach((child) => applyColourIfSet(child, groupColour));
      }
    };
    const topLevelNodes = getChildren(board, board);
    topLevelNodes.forEach((node) => applyColourIfSet(node, node.nodeProperties.groupColour ?? undefined));
  }

  private _createAdditionalData(
    boardContainer: ClientBoardContainer,
    isV5: Boolean,
    tree?: Tree
  ): DashboardAdditionalData {
    const isPanorama = this._featureFlagsService.isEnabled(ClientFeatureFlagName.Panorama);
    const calculationResult = this._dashboardCalculationService.calculate(
      boardContainer,
      tree,
      isPanorama,
      isV5 ? 'none' : 'onlyUnconnected'
    );

    const reportedEmissionsCalculationResult = isV5
      ? this._dashboardCalculationService.calculateReportedEmissions(boardContainer, tree)
      : this._dashboardCalculationService.calculateReportedEmissionsV4(boardContainer, tree);

    const labels = this._labelCreatorV4Service.createLabels(boardContainer);
    const hideEnergyModel = this._featureFlagsService.isEnabled(ClientFeatureFlagName.DashboardHideEnergyModel);
    return { calculationResult, reportedEmissionsCalculationResult, labels, hideEnergyModel };
  }

  private _createBoardContainer(data: RawBoardData): ClientBoardContainer {
    //removing any bad references on the board object
    //TODO: Find a better place for this, we filter nodes in the backend-service, but when retrieving the board there we don't have the node list to look at.
    //Perhaps we could add a similar construct as the ClientBoardContainer, i.e. that holds the related objects.
    const filteredActions = this._filterActions(data.board, data.clientActions);
    const validNodeIds = data.clientNodes.map((n) => n.id);
    const validNodes = data.board.childNodes.filter((n) => validNodeIds.includes(n));
    const board = { ...data.board, childNodes: validNodes };
    const nodes = toIdDict(
      data.clientNodes.map((node) => ({ ...node, childNodes: node.childNodes.filter((n) => validNodeIds.includes(n)) }))
    );
    const actions = toIdDict(filteredActions);
    const indicators = toIdDict(data.clientIndicators);
    const baselineInventory = getBaselineInventoryValues(data.clientInventories, board);
    if (!baselineInventory) {
      throw new Error('No baseline inventory found');
    }
    const parameters = baselineInventory.variables;
    const boardContainer = { ...board, nodes, actions, indicators, parameters };
    this._applyGroupColour(boardContainer);
    return boardContainer;
  }

  //TODO: breakout to service, a copy of this function is used in v4.resolver
  private _createBoardContainerV4(data: RawBoardData): ClientBoardContainer {
    const isPanorama = this._featureFlagsService.isEnabled(ClientFeatureFlagName.Panorama);
    const boardContainer = this._createBoardContainer(data);
    const inventories = data.clientInventories;
    const nodes = Object.entries(boardContainer.nodes).reduce((dict: Dict<ClientNode>, [nodeId, node]) => {
      if (holdsActiveCustomModel(node)) {
        const customTargetModelData: CustomTargetModelData = this._customModelDataService.createValidChartDataV4(
          node.nodeProperties.customTargetModelData,
          boardContainer,
          boardContainer.boardSettings.timeline,
          isPanorama
        );
        node = { ...node, nodeProperties: { ...node.nodeProperties, customTargetModelData } };
      }
      dict[nodeId] = node;
      return dict;
    }, {});
    return { ...boardContainer, inventories, nodes };
  }

  private _filterActions(board: ClientBoard, actions: ClientAction[]): ClientAction[] {
    // Filter out non public actions and actions with deprecated swimlanes, see TODO in _toDashboard below
    return actions.filter((a) => filterOnSwimlane(a, board)).filter((a) => !a.actionProperties.isWorkingMaterial);
  }

  private _getDraft(boardId: string, isHome: boolean = false): Observable<RawBoardData> {
    const isV3 = this._featureFlagsService.isEnabled(ClientFeatureFlagName.DashboardV3);
    const isV4 = this._featureFlagsService.isEnabled(ClientFeatureFlagName.DashboardV4);
    const isV5 = !isV3 && !isV4;
    return emitTruthyOnceAndComplete(this._boardFacadeService.board$).pipe(
      mergeMap((board) =>
        forkJoin({
          board: of(board),
          inventories: this._inventoryFacadeService.inventories$.pipe(
            filter((v) => !!v),
            take(1)
          ),
          actions: isHome
            ? this._actionsFacadeService.actionsList$.pipe(
                filter((actions) => actions !== null),
                take(1)
              )
            : this._actionsService
                .extensiveListActions({
                  boardId,
                  'Accept-Language': board.boardSettings.internationalisation.defaultLanguage ?? DEFAULT_LANGUAGE,
                })
                .pipe(map((r) => r.actions?.map((a) => this._modelV2Service.toClientActionFromActionResponse(a, [])))),
          gpcHierarchy: this._gpcHierarchy$.pipe(
            filter((h) => h !== undefined),
            take(1)
          ),
          activityHierarchy: isV5
            ? this._activityHierarchyService.getActivityHierarchyWithEmissionsInventory({
                'Accept-Language': board.boardSettings.internationalisation.defaultLanguage ?? DEFAULT_LANGUAGE,
              })
            : of({} as ApiActivityHierarchyEmissionsInventoryResponse),
          transitionElements: this._transitionElement$.pipe(
            filter((el) => !!el),
            take(1)
          ),
          transitionStoryHierarchy: this._transitionStoriesHierarchy$.pipe(
            filter((tsh) => !!tsh.hierarchy && tsh.isFull),
            take(1)
          ),
        }).pipe(
          switchMap(
            ({
              board,
              inventories,
              actions,
              gpcHierarchy,
              activityHierarchy,
              transitionElements,
              transitionStoryHierarchy,
            }) => {
              let clientNodes: ClientNode[];
              let clientIndicators: IndicatorListItem[];
              let supplyChildNodes: string[] = [];
              const { nodes, indicators, supplyElements } = createNodesAndIndicators(
                transitionElements,
                transitionStoryHierarchy.hierarchy.transitionStoryGroups,
                boardId
              );
              clientNodes = nodes.concat(supplyElements);
              supplyElements.forEach((s) => supplyChildNodes.push(s.id));
              clientIndicators = indicators;

              const tree = this._activityHierarchyTreeService.buildTree(
                gpcHierarchy as ActivityHierarchyResponse,
                board.id,
                board.title
              );
              return of({
                board: {
                  ...board,
                  childNodes: transitionStoryHierarchy.hierarchy.transitionStoryGroups.map(({ id }) => id),
                  supplyChildNodes,
                  boardSettings: { ...board.boardSettings, totalEmissions: undefined },
                }, // Remove totalEmissions to indicate it needs to be calculated elsewhere
                clientNodes,
                clientIndicators: this._clientModelService.toClientIndicators(clientIndicators),
                clientActions: actions,
                clientInventories: inventories,
                activityGroups: activityHierarchy.activityGroups ?? [],
                tree,
              });
            }
          )
        )
      )
    );
  }

  private _resolveBoard(route: ActivatedRouteDataForDashboard): Observable<RawBoardData> {
    const boardId: string = route.params.boardId;
    const isV3 = this._featureFlagsService.isEnabled(ClientFeatureFlagName.DashboardV3);
    const isHome: boolean = route.data?.isHome;
    if (!isV3) {
      if (!route.data.preview) {
        throw new Error('Trying to load non selfstanding data for v4/v5');
      }
    }
    return this._getDraft(boardId, isHome);
  }

  private _setTTTitles(nodes: ClientNode[], lang: Language): void {
    nodes.forEach((node) => {
      if (hasSelectedTE(node)) {
        const key = generateModelTranslationKey(
          ModelTranslationOutputType.TransitionElementTitle,
          node.nodeProperties.transitionElement
        );
        node.title = this._translationService.translate(lang, key);
      }
    });
  }

  private _toDashboard(data: RawBoardData, ttIconData: Dict<string>): DashboardV3 {
    const boardContainer = this._createBoardContainer(data);
    return this._dashboardV3Service.toDashboard(boardContainer, ttIconData, data.tree);
  }

  private _toDashboardV4(data: RawBoardData, isV42: boolean): DashboardV4 {
    const boardContainer = this._createBoardContainerV4(data);
    return this._dashboardV4Service.toDashboard(
      this._createAdditionalData(boardContainer, isV42, data.tree),
      boardContainer,
      data.activityGroups,
      isV42,
      this._featureFlagsService.isEnabled(ClientFeatureFlagName.Panorama)
    );
  }
}
